var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { IconSearch } from "@costar/theme-ui-icons/csg/IconSearch";
import { AsyncAutocomplete as Component, renderHighlightMatchSearchItem, } from "@costar/theme-ui-autocomplete";
var DEFAULT_MIN_INPUT_LENGTH = 3;
//inspired by: packages/common-filters/node_modules/@costar/theme-ui-autocomplete/src/helpers/default-get-option-label.ts
// except freeSolo part..
var defaultGetOptionLabel = function (option) {
    if (typeof option === "string") {
        return option;
    }
    if ("freeSolo" in option) {
        return option.freeSolo;
    }
    if ("label" in option) {
        return option.label;
    }
    return option;
};
var popupIcon = React.createElement(IconSearch, { size: "1em", color: "neutral.dark" });
var EMPTY_OBJECT = {};
function AutoCompleteBasic(props) {
    var _a;
    var autoFocus = props.autoFocus, autoHighlight = props.autoHighlight, clearOnBlur = props.clearOnBlur, _b = props.componentsProps, _componentsProps = _b === void 0 ? EMPTY_OBJECT : _b, disableClearable = props.disableClearable, disableCloseOnSelect = props.disableCloseOnSelect, doNotFilterOptions = props.doNotFilterOptions, filterSelectedOptions = props.filterSelectedOptions, fixToInputWidth = props.fixToInputWidth, _c = props.forcePopupIcon, forcePopupIcon = _c === void 0 ? (props === null || props === void 0 ? void 0 : props.value) ? "auto" : true : _c, freeSolo = props.freeSolo, getOptions = props.getOptions, groupBy = props.groupBy, hidePlaceholder = props.hidePlaceholder, initialOptions = props.initialOptions, isOptionEqualToValue = props.isOptionEqualToValue, loadingText = props.loadingText, messages = props.messages, minimumInputLength = props.minimumInputLength, multiple = props.multiple, noOptionsText = props.noOptionsText, onChange = props.onChange, onCreateFreeSoloOption = props.onCreateFreeSoloOption, renderGroup = props.renderGroup, renderPlaceHolder = props.renderPlaceHolder, size = props.size, value = props.value, onInputChange = props.onInputChange, getOptionLabel = props.getOptionLabel, id = props.id;
    var ref = useRef(undefined);
    var minInputLength = useMemo(function () { return minimumInputLength !== null && minimumInputLength !== void 0 ? minimumInputLength : DEFAULT_MIN_INPUT_LENGTH; }, [minimumInputLength]);
    var val = useRef(value);
    val.current = value;
    useEffect(function () {
        var _a;
        if (autoFocus) {
            (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [autoFocus]);
    var EMPTY_OPTIONS = useMemo(function () { return []; }, []);
    var filterOptions = useCallback(function (options, params) {
        var inputValue = params.inputValue;
        var tInputValue = inputValue === null || inputValue === void 0 ? void 0 : inputValue.trim();
        if (freeSolo && onCreateFreeSoloOption && tInputValue !== "" && tInputValue.length >= minInputLength) {
            options.unshift(onCreateFreeSoloOption(tInputValue));
        }
        return options;
    }, [freeSolo, minInputLength, onCreateFreeSoloOption]);
    var onChangeWithManualBlurring = useCallback(function (e, v, reason) {
        onChange(e, v, reason);
        if (!multiple && reason !== "clear") {
            setTimeout(function () {
                var _a;
                (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.blur();
            }, 0);
        }
    }, [multiple, onChange]);
    var componentsProps = useMemo(function () { return (__assign(__assign({}, _componentsProps), { input: __assign({ ref: ref }, _componentsProps === null || _componentsProps === void 0 ? void 0 : _componentsProps.input), popover: __assign({ marginThreshold: 10, disableScrollLock: true }, _componentsProps === null || _componentsProps === void 0 ? void 0 : _componentsProps.popover), tag: { useThemeVariant: true }, rootInput: __assign({}, _componentsProps === null || _componentsProps === void 0 ? void 0 : _componentsProps.rootInput) })); }, [_componentsProps]);
    var renderOption = useCallback(function (props, option, state) {
        var listItemStyling = {
            divider: true,
            borderBottom: "neutralsGray",
            height: "3.625rem",
        };
        var props_ = __assign(__assign({}, props), listItemStyling);
        return renderHighlightMatchSearchItem(props_, option, state);
    }, []);
    var filterOptionsOrDoNotFilter = useMemo(function () {
        if (freeSolo) {
            return filterOptions;
        }
        if (doNotFilterOptions) {
            return function (x) { return x; };
        }
        return undefined;
    }, [doNotFilterOptions, filterOptions, freeSolo]);
    var renderPlaceholderWithRef = useCallback(function (props) {
        //does box props already have a ref the input??
        return renderPlaceHolder === null || renderPlaceHolder === void 0 ? void 0 : renderPlaceHolder(__assign(__assign({}, props), { autoCompleteRef: ref }));
    }, [renderPlaceHolder]);
    return (React.createElement(Component, { "automation-id": (_a = props === null || props === void 0 ? void 0 : props["automation-id"]) !== null && _a !== void 0 ? _a : "async-autocomplete", getOptions: getOptions, forcePopupIcon: forcePopupIcon, placeholder: messages.inputPlaceholderText, minimumInputLength: minInputLength, isOptionEqualToValue: isOptionEqualToValue, popupIcon: popupIcon, size: size, onChange: onChangeWithManualBlurring, renderOption: multiple ? props.renderOption : props.renderOption || renderOption, options: initialOptions || EMPTY_OPTIONS, renderPlaceHolder: renderPlaceholderWithRef, value: value !== null && value !== void 0 ? value : null, freeSolo: freeSolo, fixToInputWidth: fixToInputWidth, filterOptions: filterOptionsOrDoNotFilter, componentsProps: componentsProps, noOptionsText: noOptionsText !== null && noOptionsText !== void 0 ? noOptionsText : messages === null || messages === void 0 ? void 0 : messages.noResults, loadingText: loadingText !== null && loadingText !== void 0 ? loadingText : messages === null || messages === void 0 ? void 0 : messages.loading, clearOnBlur: clearOnBlur !== null && clearOnBlur !== void 0 ? clearOnBlur : true, renderGroup: renderGroup, groupBy: groupBy, 
        // selectOnFocus={false} //temp disable selecting of text on focus due to bug
        openOnMouseDown: !!renderPlaceHolder, alwaysFilterOptionsMatchInputValue: true, disableClearable: disableClearable ? disableClearable : !value, getOptionLabel: getOptionLabel !== null && getOptionLabel !== void 0 ? getOptionLabel : defaultGetOptionLabel, disableCloseOnSelect: disableCloseOnSelect, multiple: multiple, autoHighlight: autoHighlight, filterSelectedOptions: filterSelectedOptions, hidePlaceholder: hidePlaceholder, onInputChange: onInputChange, openOnFocus: true, onFocus: props.onFocus, onBlur: props.onBlur, id: id }));
}
function AutoComplete(props) {
    return React.createElement(AutoCompleteBasic, __assign({}, props));
}
export { AutoComplete, AutoCompleteBasic, DEFAULT_MIN_INPUT_LENGTH, };
//# sourceMappingURL=auto-complete-basic.js.map